import { productCodeVariants } from '@iws/pos-devices'

import { db, rows } from '@/db/database'
import { requireDevice } from '@/lib/device'
import { nextInStoreCode } from '@/lib/inStoreCode'
import { newTempId } from '@/lib/tempId'
import { syncEngine } from '@/sync/SyncEngine'
import { nearestExpiryByVariation, stockByVariation } from '@/db/repositories/stock'
import { Paginated, PosHttpParams, PosModel, PosSync } from '@/types'

type Row = PosSync.LocalRow

const TONES = ['info', 'cta', 'violet', 'warning', 'success', 'error']

const live = (row: Row) => !row.deleted_at && (row.status === undefined || row.status === 'active')

/** Categories for the chips; tones rotate so neighbours differ. */
export const getCategories = async (): Promise<PosModel.Category[]> => {
  const categories = (await rows('categories').toArray()).filter(live)
  return categories
    .sort((a, b) => Number(a.sl_no ?? 0) - Number(b.sl_no ?? 0) || String(a.name).localeCompare(String(b.name)))
    .map((c, i) => ({ _id: rowRef(c), name: String(c.name), color: TONES[i % TONES.length] }))
}

/**
 * The id other rows use for this one: the server `_id` once it has one, else the device's temp id. A row made on the
 * device keeps its temp `id` after sync, while pulled rows point at its server `_id`, so lookups go by both.
 */
export const rowRef = (row: Row) => String(row._id ?? row.id)

const byBothIds = <T>(list: Row[], value: (row: Row) => T) => {
  const map = new Map<string, T>()
  for (const row of list) {
    map.set(row.id, value(row))
    if (row._id) map.set(String(row._id), value(row))
  }
  return map
}

/**
 * One sellable line per variation, priced for this branch: the branch price when there is one, else the variation's
 * regular price (§7.1; price books per customer come with the cart). Stock is the branch's total over all batches,
 * including sales on this device that have not synced yet.
 */
export const getProducts = async (query: PosHttpParams.ProductList = {}): Promise<Paginated<PosModel.Product>> => {
  const [products, variations, branchPrices, stockOf, units, expiryOf, books] = await Promise.all([
    rows('products').toArray(),
    rows('product_variations').toArray(),
    rows('branch_product_variations').toArray(),
    stockByVariation(),
    rows('measurement_units').toArray(),
    nearestExpiryByVariation(),
    rows('price_books').toArray(),
  ])
  // A deleted or inactive book prices nobody; its customers pay the normal price.
  const liveBooks = new Set(books.filter((b) => !b.deleted_at && (b.status ?? 'active') === 'active').map((b) => String(b._id ?? b.id)))
  const productById = byBothIds(products.filter(live), (p) => p)
  const unitById = byBothIds(units, (u) => String(u.title ?? 'pc'))
  const priceKey = (bp: Row) => String(bp.product_variation_id ?? bp.product_variation_temp_id)
  const branchPrice = new Map(branchPrices.filter((bp) => !bp.price_book_id).map((bp) => [priceKey(bp), Number(bp.sale_price)]))
  const bookPrices = new Map<string, Record<string, number>>()
  for (const bp of branchPrices.filter((row) => row.price_book_id && liveBooks.has(String(row.price_book_id)))) {
    bookPrices.set(priceKey(bp), { ...bookPrices.get(priceKey(bp)), [String(bp.price_book_id)]: Number(bp.sale_price) })
  }
  const ofVariation = <T>(map: Map<string, T>, v: Row) => map.get(v.id) ?? (v._id ? map.get(String(v._id)) : undefined)

  const search = query.search?.trim().toLowerCase()
  const items: PosModel.Product[] = []
  for (const v of variations.filter(live)) {
    const product = productById.get(String(v.product_id ?? v.product_temp_id))
    if (!product) continue
    const regular = Number(v.regular_price ?? 0)
    const price = ofVariation(branchPrice, v) || regular
    const item: PosModel.Product = {
      _id: v.id,
      product_id: product.id,
      name: v.name ? `${product.name} — ${v.name}` : String(product.name),
      sku: String(v.sku ?? ''),
      barcodes: Array.isArray(v.barcodes) ? v.barcodes.map(String) : [],
      plu_code: (v.plu_code as string | null | undefined) ?? null,
      category_id: String(product.category_id ?? product.category_temp_id ?? ''),
      price,
      compare_price: regular > price ? regular : undefined,
      stock: (stockOf.get(v.id) ?? 0) + (v._id && v._id !== v.id ? (stockOf.get(String(v._id)) ?? 0) : 0),
      low_stock_threshold: Number(v.min_stock_quantity ?? 0),
      unit: unitById.get(String(product.measurement_unit_id ?? product.measurement_unit_temp_id)) ?? 'pc',
      taxable: Number(product.sale_vat ?? 0) > 0,
      sale_vat: Number(product.sale_vat ?? 0),
      price_type: product.price_type === 'variable' ? 'variable' : 'fixed',
      sold_by_unit: Boolean(product.sold_by_unit),
      // Not pulled for staff without products:view_cost; left undefined so a sale doesn't report a cost of 0.
      purchase_cost: v.purchase_cost == null ? undefined : Number(v.purchase_cost),
      book_prices: ofVariation(bookPrices, v),
      pending_sync: v.synced !== 1,
      server_id: v._id ? String(v._id) : undefined,
      branch_price: ofVariation(branchPrice, v) || undefined,
      regular_price: regular,
      expiry_date: ofVariation(expiryOf, v) ?? null,
    }
    if (query.category_id && item.category_id !== query.category_id) continue
    if (query.low_stock && item.stock > item.low_stock_threshold) continue
    if (search && !`${item.name} ${item.sku} ${item.barcodes.join(' ')}`.toLowerCase().includes(search)) continue
    items.push(item)
  }
  items.sort((a, b) => a.name.localeCompare(b.name))
  return { items, total: items.length }
}

/**
 * By a value in `barcodes` (POS.md §18.8), in any of its retail forms (UPC-A / 0-led EAN-13 / UPC-E), since readers
 * report them differently. The SKU still finds a product through search, not as a scan.
 */
export const getProductByCode = async (code: string): Promise<PosModel.Product | null> => {
  const { items } = await getProducts()
  const forms = productCodeVariants(code)
  return items.find((p) => p.barcodes.some((b) => forms.includes(b))) ?? null
}

/** By `plu_code`, for weight-scale labels. */
export const getProductByPlu = async (plu: string): Promise<PosModel.Product | null> => {
  const { items } = await getProducts()
  return items.find((p) => p.plu_code === plu) ?? null
}

export interface NewProduct {
  name: string
  price: number
  price_type: 'fixed' | 'variable'
  sold_by_unit: boolean
  category_id?: string
  /** A manufacturer code scanned at the till; without one the in-store `20` code is the barcode. */
  barcode?: string
}

/**
 * A product made at the till (A9): one variation, zero stock (sales take it negative; stock in is an online action).
 * Written here and synced; the server gives it a PLU after its first sync (POS.md §18.8). The in-store `20` code is
 * also its SKU, which must be unique and needs no server.
 */
export const createProduct = async (body: NewProduct): Promise<PosModel.Product> => {
  const device = requireDevice()
  const barcode = body.barcode?.trim()
  if (barcode) {
    const taken = (await rows('product_variations').toArray()).find((v) => Array.isArray(v.barcodes) && v.barcodes.includes(barcode))
    if (taken) throw new Error(`Barcode ${barcode} is already used by ${taken.name || taken.sku}.`)
  }
  const code = await nextInStoreCode('20')
  const createdAt = new Date().toISOString()
  const productId = newTempId(device.terminal_unique_no)
  const variationId = newTempId(device.terminal_unique_no)
  await db.transaction('rw', rows('products'), rows('product_variations'), async () => {
    await rows('products').add({
      id: productId,
      temp_id: productId,
      synced: 0,
      name: body.name,
      category_id: body.category_id || null,
      price_type: body.price_type,
      sold_by_unit: body.sold_by_unit,
      delivery_return_policy: '',
      status: 'active',
      created_at: createdAt,
    })
    await rows('product_variations').add({
      id: variationId,
      temp_id: variationId,
      synced: 0,
      product_id: productId,
      product_temp_id: productId,
      sku: code,
      barcodes: [barcode || code],
      regular_price: body.price,
      min_stock_quantity: 0,
      status: 'active',
      created_at: createdAt,
    })
  })
  syncEngine.requestSync()
  const created = (await getProducts()).items.find((p) => p._id === variationId)
  if (!created) throw new Error('The product could not be saved.')
  return created
}
